import { Plus, Star } from 'lucide-react'
import type { MarketSegmentWithScore } from '@ispot/shared'

interface Props {
  segments: MarketSegmentWithScore[]
  selectedId: string | null
  beachheadId: string | null
  onSelect: (id: string) => void
  onAddClick: () => void
}

export function SegmentList({
  segments,
  selectedId,
  beachheadId,
  onSelect,
  onAddClick,
}: Props) {
  return (
    <div
      className="flex h-full w-[280px] shrink-0 flex-col border-r"
      style={{
        borderColor: 'var(--color-border-subtle)',
        backgroundColor: 'var(--color-warm-50)',
      }}
    >
      <header
        className="flex items-center justify-between border-b px-4 py-3"
        style={{ borderColor: 'var(--color-border-subtle)' }}
      >
        <div>
          <h3
            className="text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Segments
          </h3>
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {segments.length} of 20
          </p>
        </div>
        <button
          type="button"
          onClick={onAddClick}
          className="inline-flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-[var(--color-warm-200)]"
          title="Add segment"
          aria-label="Add segment"
        >
          <Plus className="h-4 w-4" style={{ color: 'var(--color-text)' }} />
        </button>
      </header>

      <ul className="flex-1 overflow-y-auto px-2 py-2">
        {segments.length === 0 ? (
          <li
            className="rounded-lg px-3 py-6 text-center text-xs italic"
            style={{ color: 'var(--color-text-muted)' }}
          >
            No segments yet.
            <br />
            Click + to add one.
          </li>
        ) : (
          segments.map((seg) => {
            const active = seg.id === selectedId
            const beachhead = seg.id === beachheadId
            return (
              <li key={seg.id} className="mb-1">
                <button
                  type="button"
                  onClick={() => onSelect(seg.id)}
                  className="w-full rounded-md px-3 py-2 text-left transition-colors"
                  style={{
                    backgroundColor: active ? 'var(--color-warm-200)' : 'transparent',
                    borderLeft: beachhead ? '3px solid var(--color-business, #0984e3)' : '3px solid transparent',
                    paddingLeft: beachhead ? 10 : 12,
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="truncate text-xs font-semibold"
                          style={{ color: 'var(--color-text)' }}
                        >
                          {seg.name}
                        </span>
                        {beachhead ? (
                          <Star
                            className="h-2.5 w-2.5 shrink-0"
                            style={{ color: 'var(--color-business, #0984e3)', fill: 'var(--color-business, #0984e3)' }}
                          />
                        ) : null}
                      </div>
                      <p
                        className="truncate text-[10px]"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {seg.sector} · {seg.vertical || 'no vertical'}
                      </p>
                    </div>
                    <ScoreBadge value={seg.weightedScore} />
                  </div>
                </button>
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}

function ScoreBadge({ value }: { value: number }) {
  if (value === 0) {
    return (
      <span
        className="text-xs font-medium"
        style={{ color: 'var(--color-text-muted)' }}
      >
        —
      </span>
    )
  }
  const color =
    value >= 7
      ? 'var(--color-market)'
      : value >= 4
        ? '#a17a1f'
        : '#b14132'
  return (
    <span className="font-display text-sm font-bold" style={{ color }}>
      {value.toFixed(1)}
    </span>
  )
}
